<template>
  <!--
    Backup restore (permission BACKUP_RESTORE). The old server crashed on
    23 Sep 2026; production runs on a copy restored from the 17 Sep backup.
    This page browses the old server's data since 16 Sep, shows per record
    whether it exists in the current system, and restores exactly what the
    user picks. The source is read-only; nothing current is deleted.
  -->
  <div class="bk-root">
    <!-- ============ HEADER ============ -->
    <b-card class="bk-head-card">
      <div class="bk-head">
        <div class="bk-head__icon"><i class="fa-solid fa-clock-rotate-left"></i></div>
        <div class="bk-head__text">
          <h4 class="bk-head__title">{{ $t('backup.title') }}</h4>
          <div class="bk-head__sub">
            <span><i class="fa-solid fa-server"></i>{{ $t('backup.source') }}
              <b>{{ info && info.source ? info.source.host : '—' }}</b></span>
            <span v-if="info && info.source && (info.source.crmDatabase || info.source.coreDatabase)" class="bk-head__dbs">
              {{ [info.source.crmDatabase, info.source.coreDatabase].filter(Boolean).join(' · ') }}
            </span>
            <span><i class="fa-regular fa-calendar"></i>{{ $t('backup.dataSince', { date: sinceLabel }) }}</span>
          </div>
        </div>
        <div class="bk-head__actions">
          <span class="bk-reach" :class="`bk-reach--${reachState}`">
            <span class="bk-reach__dot"></span>{{ $t(`backup.reach.${reachState}`) }}
          </span>
          <b-button size="sm" variant="outline-primary" :disabled="infoLoading" @click="refreshAll">
            <i class="fa-solid fa-rotate-right mr-50" :class="{ 'fa-spin': infoLoading }"></i>{{ $t('backup.refresh') }}
          </b-button>
        </div>
      </div>

      <div class="bk-safety">
        <span><i class="fa-solid fa-lock"></i>{{ $t('backup.safety.readOnly') }}</span>
        <span><i class="fa-solid fa-shield-halved"></i>{{ $t('backup.safety.noDelete') }}</span>
        <span><i class="fa-solid fa-scale-unbalanced"></i>{{ $t('backup.safety.balance') }}</span>
      </div>

      <!-- Unreachable / error banner -->
      <div v-if="reachState === 'down' || reachState === 'error'" class="bk-down">
        <i class="fa-solid fa-plug-circle-xmark"></i>
        <div>
          <!-- backend: configured=false → BACKUP_MONGO_URI not set on the CRM server -->
          <template v-if="info && info.configured === false">
            <div class="bk-down__title">{{ $t('backup.notConfiguredTitle') }}</div>
            <div class="bk-down__text">{{ $t('backup.notConfiguredText') }}</div>
          </template>
          <!-- backup answers, but building the comparison failed -->
          <template v-else-if="reachState === 'error'">
            <div class="bk-down__title">{{ $t('backup.compareFailedTitle') }}</div>
            <div class="bk-down__text">{{ $t('backup.compareFailedText') }}</div>
          </template>
          <template v-else>
            <div class="bk-down__title">{{ $t('backup.unreachableTitle') }}</div>
            <div class="bk-down__text">{{ infoError || $t('backup.unreachableText', { host: info && info.source ? info.source.host : '' }) }}</div>
          </template>
          <!-- exact technical cause from the backend (no credentials) -->
          <div v-if="info && info.errorDetail" class="bk-down__detail">{{ info.errorDetail }}</div>
        </div>
        <b-button size="sm" variant="danger" class="ml-auto" @click="refreshAll">
          <i class="fa-solid fa-rotate-right mr-50"></i>{{ $t('backup.retry') }}
        </b-button>
      </div>

      <!-- ============ KPI STRIP ============ -->
      <div class="bk-kpis">
        <div v-for="k in kpis" :key="k.entity" class="bk-kpi">
          <div class="bk-kpi__top">
            <span class="bk-kpi__icon" :class="`bk-kpi__icon--${k.entity}`"><i :class="k.icon"></i></span>
            <div class="bk-kpi__label">{{ $t(`backup.kpi.${k.entity}`) }}</div>
            <div class="bk-kpi__total">
              <template v-if="infoLoading && !info"><b-skeleton width="36px" height="22px"/></template>
              <template v-else>{{ k.known ? k.total : '—' }}</template>
            </div>
          </div>
          <div class="bk-kpi__todo">
            <template v-if="!k.known">&nbsp;</template>
            <template v-else-if="k.todo">{{ $tc('backup.kpi.toRestore', k.todo, { n: k.todo }) }}</template>
            <template v-else-if="k.total"><i class="fa-solid fa-circle-check text-success mr-25"></i>{{ $t('backup.kpi.allClear') }}</template>
            <template v-else>&nbsp;</template>
          </div>
          <div class="bk-kpi__bar">
            <span v-for="seg in k.segments" :key="seg.status" class="bk-kpi__seg"
                  :class="`bk-kpi__seg--${seg.status.toLowerCase()}`" :style="{ flexGrow: seg.n }"
                  v-b-tooltip.hover.top="`${bkStatusLabel(seg.status)}: ${seg.n}`"></span>
          </div>
          <div v-if="k.known" class="bk-kpi__pills">
            <button v-for="seg in k.pills" :key="seg.status" type="button" class="bk-kpi__pill"
                    :class="[`bk-kpi__pill--${seg.status.toLowerCase()}`, { 'is-zero': !seg.n, 'is-static': !canJump(k.entity, seg.status) }]"
                    :disabled="!seg.n || !canJump(k.entity, seg.status)"
                    v-b-tooltip.hover.top="canJump(k.entity, seg.status) ? $t('backup.kpi.jumpHint') : ''"
                    @click="jump(k.entity, seg.status)">
              {{ bkStatusLabel(seg.status) }}<b>{{ seg.n }}</b>
            </button>
          </div>
        </div>
      </div>
    </b-card>

    <!-- ============ TABS ============ -->
    <b-card no-body class="bk-tabs-card">
      <b-tabs v-model="tabIndex" card nav-class="bk-nav" content-class="bk-tab-content">
        <b-tab v-for="t in tabs" :key="t.key">
          <template #title>
            <i :class="t.icon" class="mr-50"></i>{{ $t(`backup.tabs.${t.key}`) }}
            <span v-if="todoOf(t.key)" class="bk-nav__n">{{ todoOf(t.key) }}</span>
          </template>

          <template v-if="visited[t.key]">
            <backup-filter-bar v-model="filters[t.key]" :entity="t.key" :since="info && info.since"
                               :counts="countsOf(t.key)"/>
            <backup-clients-table v-if="t.key === 'clients'" ref="clients" :filters="filters.clients"
                                  @open-client="openWorkspace"/>
            <backup-monetary-table v-else-if="t.key === 'monetary'" ref="monetary" :filters="filters.monetary"
                                   @open-client="openWorkspace" @restore-plan="openConfirm"/>
            <backup-trades-table v-else-if="t.key === 'trades'" ref="trades" :filters="filters.trades"
                                 @open-client="openWorkspace" @restore-plan="openConfirm"/>
            <backup-notes-table v-else-if="t.key === 'notes'" ref="notes" :filters="filters.notes"
                                @open-client="openWorkspace" @restore-plan="openConfirm"/>
            <backup-restore-log-table v-else-if="t.key === 'log'" ref="log" :filters="filters.log"/>
          </template>
        </b-tab>
      </b-tabs>
    </b-card>

    <backup-client-workspace ref="workspace" @restore-plan="openConfirm"/>
    <backup-restore-confirm ref="confirm" @done="onRestored"/>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinBackup, { MATCH_STATUSES, RESTORABLE_STATUSES } from '@/components/mixin/mixinBackup'
import BackupFilterBar from '@/components/partial/backup/BackupFilterBar.vue'
import BackupClientsTable from '@/components/tables/BackupClientsTable.vue'
import BackupMonetaryTable from '@/components/tables/BackupMonetaryTable.vue'
import BackupTradesTable from '@/components/tables/BackupTradesTable.vue'
import BackupNotesTable from '@/components/tables/BackupNotesTable.vue'
import BackupRestoreLogTable from '@/components/tables/BackupRestoreLogTable.vue'
import BackupClientWorkspace from '@/components/modal/backup/BackupClientWorkspace.vue'
import BackupRestoreConfirm from '@/components/modal/backup/BackupRestoreConfirm.vue'

const TABS = [
  { key: 'clients', icon: 'fa-solid fa-user-group' },
  { key: 'monetary', icon: 'fa-solid fa-coins' },
  { key: 'trades', icon: 'fa-solid fa-chart-line' },
  { key: 'notes', icon: 'fa-regular fa-note-sticky' },
  { key: 'log', icon: 'fa-solid fa-list-check' },
]
const KPI = [
  { entity: 'clients', icon: 'fa-solid fa-user-group' },
  { entity: 'monetary', icon: 'fa-solid fa-coins' },
  { entity: 'trades', icon: 'fa-solid fa-chart-line' },
  { entity: 'notes', icon: 'fa-regular fa-note-sticky' },
  { entity: 'accounts', icon: 'fa-solid fa-id-card' },
]

export default {
  name: 'BackupRestoreView',
  components: {
    BackupFilterBar,
    BackupClientsTable,
    BackupMonetaryTable,
    BackupTradesTable,
    BackupNotesTable,
    BackupRestoreLogTable,
    BackupClientWorkspace,
    BackupRestoreConfirm,
  },
  mixins: [mixinNotifications, mixinBackup],
  data: () => ({
    tabs: TABS,
    tabIndex: 0,
    visited: { clients: true },
    info: null,
    infoLoading: false,
    infoError: null,
    infoSeq: 0,
    filters: { clients: {}, monetary: {}, trades: {}, notes: {}, log: {} },
  }),
  computed: {
    reachState() {
      if (this.infoError) return 'down'
      if (!this.info) return 'checking'
      if (this.info.reachable === false) return 'down'
      return this.info.error ? 'error' : 'up'
    },
    sinceLabel() {
      return moment((this.info && this.info.since) || '2026-09-16').format('D MMM YYYY')
    },
    kpis() {
      const counts = (this.info && this.info.counts) || {}
      return KPI.map(k => {
        const known = this.reachState === 'up' && counts[k.entity] != null
        const c = counts[k.entity] || {}
        const statuses = MATCH_STATUSES.filter(s => s !== 'CHANGED' || k.entity === 'clients')
        const pills = statuses.map(status => ({ status, n: Number(c[status]) || 0 }))
        return {
          ...k,
          known,
          total: pills.reduce((t, p) => t + p.n, 0),
          todo: pills.filter(p => RESTORABLE_STATUSES.includes(p.status) || p.status === 'CHANGED').reduce((t, p) => t + p.n, 0),
          pills,
          segments: pills.filter(p => p.n > 0),
        }
      })
    },
  },
  watch: {
    tabIndex(i) {
      const key = TABS[i] && TABS[i].key
      if (key && !this.visited[key]) this.visited = { ...this.visited, [key]: true }
    },
  },
  created() {
    this.loadInfo()
    // Client status names/colours (resolved in rows and field differences).
    if (!(this.allStatuses || []).length) {
      this.$store.dispatch('clients/refreshData').catch(() => {})
    }
  },
  methods: {
    ...mapActions('backup', ['getBackupInfo']),
    async loadInfo() {
      const seq = ++this.infoSeq
      this.infoLoading = true
      try {
        const info = await this.getBackupInfo()
        if (seq !== this.infoSeq) return
        this.info = info || null
        this.infoError = null
      } catch (err) {
        if (seq !== this.infoSeq) return
        this.infoError = this.getErrorDescription(err)
      } finally {
        if (seq === this.infoSeq) this.infoLoading = false
      }
    },
    tableRef(key) {
      const r = this.$refs[key]
      return Array.isArray(r) ? r[0] : r
    },
    refreshAll() {
      this.loadInfo()
      TABS.forEach(t => {
        const table = this.tableRef(t.key)
        if (table) table.refresh()
      })
    },
    countsOf(key) {
      return (this.info && this.info.counts && this.info.counts[key]) || null
    },
    todoOf(key) {
      if (key === 'log') return 0
      const k = this.kpis.find(x => x.entity === key)
      return k ? k.todo : 0
    },
    canJump(entity, status) {
      if (entity === 'accounts') return RESTORABLE_STATUSES.includes(status)
      return true
    },
    // KPI pill → its tab, filtered by that status. Accounts have no tab: their
    // missing ones are reached through "clients with missing items".
    jump(entity, status) {
      if (!this.canJump(entity, status)) return
      if (entity === 'accounts') {
        this.filters = { ...this.filters, clients: { ...this.filters.clients, statuses: [], onlyWithMissingItems: true } }
        this.tabIndex = TABS.findIndex(t => t.key === 'clients')
        return
      }
      this.filters = { ...this.filters, [entity]: { ...this.filters[entity], statuses: [status] } }
      this.tabIndex = TABS.findIndex(t => t.key === entity)
    },
    openWorkspace(clientBackupId) {
      if (clientBackupId == null || clientBackupId === '') return
      this.$refs.workspace.open(clientBackupId)
    },
    openConfirm(plan) {
      if (!plan || !plan.requests || !plan.requests.length) return
      this.$refs.confirm.open(plan)
    },
    // Statuses change across entities after any restore (a restored client
    // unlocks its items, etc.) — refresh counts, every loaded tab and the
    // workspace if it's open.
    onRestored({ plan }) {
      this.loadInfo()
      TABS.forEach(t => {
        const table = this.tableRef(t.key)
        if (!table) return
        if (plan && plan.source === t.key && typeof table.afterRestore === 'function') table.afterRestore()
        else table.refresh()
      })
      if (plan && plan.source === 'workspace') this.$refs.workspace.reload()
    },
  },
}
</script>

<!-- Un-scoped: shared by the tables and the modals (which render in <body>). -->
<style lang="scss">
@import './backup-restore';
</style>

<style scoped lang="scss">
.bk-head {
  display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;
  &__icon {
    width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 1.35rem; color: #fff;
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    box-shadow: 0 10px 22px -12px rgba(115, 103, 240, 0.7);
  }
  &__text { min-width: 0; }
  &__title { margin-bottom: 0.15rem; font-weight: 800; }
  &__sub {
    display: flex; flex-wrap: wrap; gap: 0.35rem 1rem; font-size: 0.84rem; color: #9a9dad;
    i { margin-right: 0.35rem; }
    b { color: #5e5873; }
  }
  &__dbs { font-family: monospace; font-size: 0.78rem; }
  &__actions { margin-left: auto; display: flex; align-items: center; gap: 0.6rem; }
}

.bk-reach {
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.25rem 0.7rem; border-radius: 999px; font-size: 0.78rem; font-weight: 700;
  &__dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
  &--up { color: #1f9d57; background: rgba(40, 199, 111, 0.12); }
  &--up .bk-reach__dot { box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.25); }
  &--down { color: #ea5455; background: rgba(234, 84, 85, 0.12); }
  &--error { color: #ff9f43; background: rgba(255, 159, 67, 0.14); }
  &--checking { color: #82868b; background: rgba(130, 134, 139, 0.12); }
  &--checking .bk-reach__dot { animation: bk-pulse 1s infinite alternate; }
}
@keyframes bk-pulse { from { opacity: 0.3; } to { opacity: 1; } }

.bk-safety {
  display: flex; flex-wrap: wrap; gap: 0.4rem 1.2rem; margin-top: 0.9rem;
  padding: 0.5rem 0.8rem; border-radius: 10px;
  background: rgba(40, 199, 111, 0.06); border: 1px dashed rgba(40, 199, 111, 0.35);
  font-size: 0.8rem; color: #4b7d61;
  i { margin-right: 0.4rem; color: #28c76f; }
}

.bk-down {
  display: flex; align-items: center; gap: 0.9rem; margin-top: 0.9rem;
  padding: 0.8rem 1rem; border-radius: 12px;
  background: rgba(234, 84, 85, 0.08); border: 1px solid rgba(234, 84, 85, 0.35);
  > i { font-size: 1.6rem; color: #ea5455; }
  &__title { font-weight: 800; color: #ea5455; }
  &__text { font-size: 0.84rem; color: #6e6b7b; }
}

.bk-kpis {
  display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 0.8rem; margin-top: 1rem;
}
.bk-kpi {
  padding: 0.8rem 0.85rem 0.75rem; border-radius: 12px;
  border: 1px solid rgba(110, 117, 159, 0.16); background: rgba(115, 103, 240, 0.025);
  &__top { display: flex; align-items: center; gap: 0.55rem; }
  &__icon {
    width: 32px; height: 32px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.9rem; color: #7367f0; background: rgba(115, 103, 240, 0.12);
  }
  &__label { font-weight: 700; color: #5e5873; font-size: 0.9rem; }
  &__total { margin-left: auto; font-size: 1.35rem; font-weight: 800; color: #5e5873; line-height: 1; }
  &__todo { margin-top: 0.35rem; font-size: 0.76rem; font-weight: 700; color: #7367f0; min-height: 1.1em; }
  &__bar {
    display: flex; gap: 2px; height: 6px; margin: 0.45rem 0 0.55rem; border-radius: 4px; overflow: hidden;
    background: rgba(110, 117, 159, 0.12);
  }
  &__seg {
    flex-basis: 0; min-width: 4px; border-radius: 4px;
    &--missing { background: #7367f0; }
    &--id_conflict { background: #ff9f43; }
    &--changed { background: #00cfe8; }
    &--exists { background: #b9b9c3; }
    &--restored { background: #28c76f; }
  }
  &__pills { display: flex; flex-wrap: wrap; gap: 0.25rem; }
  &__pill {
    display: inline-flex; align-items: center; gap: 0.3rem;
    padding: 0.08rem 0.45rem; border-radius: 999px; border: 1px solid transparent;
    font-size: 0.7rem; font-weight: 600; cursor: pointer; background: transparent;
    b { font-weight: 800; }
    &--missing { color: #7367f0; background: rgba(115, 103, 240, 0.1); }
    &--id_conflict { color: #e8862a; background: rgba(255, 159, 67, 0.13); }
    &--changed { color: #00a8bd; border-color: rgba(0, 207, 232, 0.5); }
    &--exists { color: #82868b; background: rgba(130, 134, 139, 0.1); }
    &--restored { color: #1f9d57; background: rgba(40, 199, 111, 0.12); }
    &:not(:disabled):hover { border-color: currentColor; }
    &.is-zero { opacity: 0.45; cursor: default; }
    &.is-static { cursor: default; }
  }
}

.bk-tabs-card ::v-deep .card-header { padding-bottom: 0; }
.bk-tabs-card ::v-deep .bk-nav .nav-link { font-weight: 600; }
.bk-nav__n {
  margin-left: 0.4rem; padding: 0 0.45rem; border-radius: 999px;
  font-size: 0.7rem; font-weight: 800; color: #fff; background: #7367f0;
}

body.dark-layout {
  .bk-head__sub b, .bk-kpi__label, .bk-kpi__total { color: #d0d2d6; }
  .bk-kpi { background: rgba(255, 255, 255, 0.02); border-color: rgba(255, 255, 255, 0.08); }
  .bk-kpi__icon { color: #a39cf6; }
  .bk-kpi__todo { color: #a39cf6; }
  .bk-kpi__seg--exists { background: #676d7d; }
  .bk-kpi__pill--missing { color: #a39cf6; }
  .bk-kpi__pill--exists { color: #a3a6b4; }
  .bk-safety { color: #8fd3ad; background: rgba(40, 199, 111, 0.05); }
  .bk-down__text { color: #b4b7bd; }
}

@media (max-width: 1399px) {
  .bk-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 767px) {
  .bk-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .bk-head__actions { margin-left: 0; width: 100%; }
}
</style>
